import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { useParams } from "react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { getCandidateAssessmentResults } from "@/api/assessments";
import { CandidateAssessmentResult } from "@/types";
import { StatusBadge } from "@/components/ui/status-badge";

export function ViewCandidateAssessment() {
  const { candidateId } = useParams();
  const { activeTeam } = useAuth();
  const [result, setResult] = useState<CandidateAssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!candidateId || !activeTeam) {
        setError("Missing candidate ID or active team");
        setLoading(false);
        return;
      }

      try {
        const data = await getCandidateAssessmentResults(candidateId, activeTeam.id);
        setResult(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load assessment results"
        );
      } finally {
        setLoading(false);
      }
    }

    if (activeTeam) {
      fetchData();
    }
  }, [candidateId, activeTeam]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <Card className="p-6">
          <div className="text-red-500">{error}</div>
        </Card>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="container mx-auto p-4">
        <Card className="p-6">
          <div>Assessment not found</div>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="container mx-auto p-4">
      <Button
        variant="outline"
        onClick={() => window.history.back()}
        className="mb-4"
      >
        &larr; Back
      </Button>

      <Card className="p-6 mb-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              {result.assessment.title}
            </h1>
            <p className="text-gray-600 mb-1">
              {result.assessment.job_specification.title}
            </p>
            <p className="text-sm text-gray-500">
              Candidate: {result.candidate.email}
            </p>
          </div>
          <StatusBadge status={result.candidate.status} capitalize={true} />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
          <div>
            <p className="text-gray-500">Invited</p>
            <p>{formatDate(result.candidate.invited_at)}</p>
          </div>
          <div>
            <p className="text-gray-500">Started</p>
            <p>{formatDate(result.candidate.started_at)}</p>
          </div>
          <div>
            <p className="text-gray-500">Completed</p>
            <p>{formatDate(result.candidate.completed_at)}</p>
          </div>
          <div>
            <p className="text-gray-500">Time Taken</p>
            <p>
              {result.candidate.started_at && result.candidate.completed_at
                ? `${Math.round(
                    (new Date(result.candidate.completed_at).getTime() -
                      new Date(result.candidate.started_at).getTime()) /
                      60000
                  )} minutes`
                : "N/A"}
            </p>
          </div>
        </div>
      </Card>

      <h2 className="text-xl font-semibold mb-4">Assessment responses</h2>

      {result.assessment.questions.map((question, index) => {
        const questionId = question.id;
        const answer = result.answers?.[questionId] || "No response";

        return (
          <Card key={questionId} className="p-6 mb-4">
            <div className="mb-4">
              <div className="text-sm text-gray-500 mb-1">
                Question {index + 1}
              </div>
              <h3 className="text-lg font-medium mb-2">{question.text}</h3>

              {question.type === "multiple-choice" && (
                <div className="text-sm text-gray-600 mb-4">
                  <div className="font-medium">Options:</div>
                  <ul className="list-disc pl-5">
                    {question.options?.map((option, i) => (
                      <li
                        key={i}
                        className={
                          answer === option ? "font-medium text-primary" : ""
                        }
                      >
                        {option} {answer === option && "✓"}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <div className="text-sm text-gray-500 mb-1">Response</div>
              <div className="p-3 bg-gray-50 rounded-md whitespace-pre-wrap">
                {answer}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
